"use client";

import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const treatments = [{
  title: "Airport Pickup Process",
  description: "We monitor your flight from the moment it departs. Your chauffeur arrives at the terminal ahead of schedule, meets you at arrivals, and handles your luggage. You walk out and your vehicle is there.",
  benefits: ["Real-time flight tracking", "Professional meet & greet", "Luggage assistance", "Curbside or inside pickup"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/airport_pickup_process.png",
  alt: "Professional chauffeur assisting a passenger with luggage at the airport terminal"
}, {
  title: "Executive Ride in the Bay Area",
  description: "Navigate Silicon Valley and San Francisco in a quiet, mobile office environment. Our vehicles are equipped for productivity — Wi‑Fi, charging ports, privacy partitions, and a professional behind the wheel.",
  benefits: ["Wi‑Fi equipped vehicles", "Privacy partitions", "Charging ports", "Discreet professional drivers"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/bay_area_executive_ride.png",
  alt: "Interior of a luxury executive vehicle set up as a mobile office with Wi-Fi and charging ports"
}, {
  title: "Corporate Event Transportation",
  description: "Coordinating transportation for board meetings, galas, and corporate events across the Bay Area. We manage complex manifests with precision timing so your guests arrive on schedule and in style.",
  benefits: ["Dedicated dispatcher", "Manifest management", "Uniformed chauffeurs", "VIP fleet selection"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/corporate_event_transportation.png",
  alt: "Fleet of luxury black cars lined up for a high-profile corporate event in San Francisco"
}];
export default function SpecializedTreatments() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="20" id="seegoldsteerinaction" ref={ref} className="bg-background py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("mb-16 text-center transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight font-sans">
            Our Services in Detail
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans">
            Every journey is prepared in advance. Every detail is considered before you arrive.
          </p>
        </div>

        <div className="flex flex-col gap-20 md:gap-32">
          {treatments.map((treatment, index) => {
          const isEven = index % 2 === 0;
          return <div key={index} data-index={index} className={cn("flex flex-col md:flex-row items-center gap-8 md:gap-16 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                {}
                <div className={cn("w-full md:w-1/2 relative group", isEven ? "md:order-1" : "md:order-2")}>
                  <div className="relative aspect-[16/9] overflow-hidden rounded-sm shadow-2xl bg-muted border-t-2 border-primary/20">
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500 z-10" />
                    <img src={treatment.image} alt={treatment.alt} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                  </div>
                  {}
                  <div className={cn("absolute -inset-4 -z-10 rounded-sm bg-primary/5 opacity-0 md:opacity-100 transition-opacity", isEven ? "-rotate-1" : "rotate-1")} />
                </div>

                {}
                <div className={cn("w-full md:w-1/2 flex flex-col justify-center", isEven ? "md:order-2" : "md:order-1")}>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 font-sans tracking-wide">
                    {treatment.title}
                  </h3>
                  <p className="text-muted-foreground text-lg mb-8 leading-relaxed font-sans">
                    {treatment.description}
                  </p>

                  <div className="mb-8">
                    <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
                      Service Highlights
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {treatment.benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-foreground/90">
                          <div className="mt-1 bg-primary/20 p-1 rounded-full text-primary shrink-0">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </div>
                          <span className="text-sm font-medium font-sans">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="pt-2">
                    <Button asChild size="lg" className="rounded-sm px-10 bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg">
                      <Link to="#">
                        Book Your Experience
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}